import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import {shuffle} from '../common/random';
import PlayerSetup from '../common/PlayerSetup';
import CategoryChips from '../common/CategoryChips';
import {useAiContent} from '../common/useAiContent';
import CollectAnswers, {CollectedAnswer} from '../common/CollectAnswers';
import VoteAmongOptions, {VoteOption, VoteRecord} from '../common/VoteAmongOptions';
import {CUSTOM_CATEGORY, FibbageItem} from './fibbageQuestions';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_KEY = 'fibbage_custom_questions';
const DISABLED_CATEGORIES_KEY = 'fibbage_disabled_categories';

type Stage = 'setup' | 'collecting' | 'voting' | 'result';

interface AiQuestion {
    question: string;
    answer: string;
    category: string;
}

const FibbageGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customQuestions, setCustomQuestions] = useState<FibbageItem[]>(() => loadJSON(CUSTOM_KEY, []));
    const [disabledCategories, setDisabledCategories] = useState<string[]>(() => loadJSON(DISABLED_CATEGORIES_KEY, []));
    const [newQuestion, setNewQuestion] = useState('');
    const [newAnswer, setNewAnswer] = useState('');

    const [stage, setStage] = useState<Stage>('setup');
    const [currentQuestion, setCurrentQuestion] = useState<FibbageItem | null>(null);
    const [usedQuestions, setUsedQuestions] = useState<Set<string>>(new Set());
    const [options, setOptions] = useState<VoteOption[]>([]);
    const [votes, setVotes] = useState<VoteRecord[]>([]);
    const [scores, setScores] = useState<Record<string, number>>({});
    const [roundPoints, setRoundPoints] = useState<Record<string, number>>({});

    const aiQuestions = useAiContent<AiQuestion>('fibbage-questions.json');

    const categories = useMemo(() => {
        const set = new Set(aiQuestions.map(q => q.category));
        return Array.from(set);
    }, [aiQuestions]);

    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        aiQuestions.forEach(q => {
            counts[q.category] = (counts[q.category] || 0) + 1;
        });
        return counts;
    }, [aiQuestions]);

    const allQuestions = useMemo((): FibbageItem[] => [
        ...aiQuestions.map((q): FibbageItem => ({question: q.question, answer: q.answer, category: q.category, ai: true})),
        ...customQuestions.map((q): FibbageItem => ({...q, category: CUSTOM_CATEGORY})),
    ], [aiQuestions, customQuestions]);

    const activeQuestions = useMemo(
        () => allQuestions.filter(q => q.custom || !disabledCategories.includes(q.category || '')),
        [allQuestions, disabledCategories]
    );

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const toggleCategory = (category: string) => {
        const next = disabledCategories.includes(category)
            ? disabledCategories.filter(c => c !== category)
            : [...disabledCategories, category];
        setDisabledCategories(next);
        saveJSON(DISABLED_CATEGORIES_KEY, next);
    };

    const addCustomQuestion = () => {
        const question = newQuestion.trim();
        const answer = newAnswer.trim();
        if (!question || !answer || !question.includes('____')) return;
        const next = [...customQuestions, {question, answer, category: CUSTOM_CATEGORY, custom: true}];
        setCustomQuestions(next);
        saveJSON(CUSTOM_KEY, next);
        setNewQuestion('');
        setNewAnswer('');
    };

    const removeCustomQuestion = (question: string) => {
        const next = customQuestions.filter(q => q.question !== question);
        setCustomQuestions(next);
        saveJSON(CUSTOM_KEY, next);
    };

    const pickQuestion = (pool: FibbageItem[], used: Set<string>): {item: FibbageItem; nextUsed: Set<string>} => {
        let available = pool.filter(q => !used.has(q.question));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const item = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(item.question);
        return {item, nextUsed: updatedUsed};
    };

    const beginRound = (pool: FibbageItem[], used: Set<string>) => {
        const {item, nextUsed} = pickQuestion(pool, used);
        setCurrentQuestion(item);
        setUsedQuestions(nextUsed);
        setOptions([]);
        setVotes([]);
        setRoundPoints({});
        setStage('collecting');
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || activeQuestions.length === 0) return;
        setScores(Object.fromEntries(players.map(p => [p.id, 0])));
        beginRound(activeQuestions, new Set());
    };

    const nextRound = () => beginRound(activeQuestions, usedQuestions);

    const backToSetup = () => {
        setStage('setup');
        setCurrentQuestion(null);
    };

    const handleCollectDone = (answers: CollectedAnswer[]) => {
        if (!currentQuestion) return;
        const opts: VoteOption[] = shuffle([
            {id: 'truth', text: currentQuestion.answer, authorId: null},
            ...answers.map((a): VoteOption => ({id: a.playerId, text: a.text, authorId: a.playerId})),
        ]);
        setOptions(opts);
        setStage('voting');
    };

    const handleVoteDone = (voteRecords: VoteRecord[]) => {
        setVotes(voteRecords);
        const points: Record<string, number> = {};
        voteRecords.forEach(v => {
            const opt = options.find(o => o.id === v.optionId);
            if (!opt) return;
            const winnerId = opt.authorId === null ? v.playerId : opt.authorId;
            points[winnerId] = (points[winnerId] || 0) + 1;
        });
        setRoundPoints(points);
        setScores(s => {
            const next = {...s};
            Object.entries(points).forEach(([pid, pts]) => {
                next[pid] = (next[pid] || 0) + pts;
            });
            return next;
        });
        setStage('result');
    };

    const ranking = useMemo(
        () => [...players].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)),
        [players, scores]
    );

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Правда или подстава</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Показывается вопрос с пропуском — настоящий ответ спрятан. Каждый придумывает
                            правдоподобный фейковый ответ, потом все варианты перемешиваются, и каждый голосует,
                            какой из них правда. Очко за угаданную правду и за каждого, кого обманул твой фейк.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Категории ({activeQuestions.length} вопросов активно)</h3>

                            {categories.length === 0 && customQuestions.length === 0 && (
                                <p className="GmHint GmHint--warn">
                                    Пока нет ни одного вопроса. Сгенерируйте пул скриптом
                                    scripts/generate-ai-content.js fibbage all 30 или добавьте свои вопросы ниже.
                                </p>
                            )}

                            {categories.length > 0 && (
                                <CategoryChips
                                    categories={categories}
                                    counts={categoryCounts}
                                    disabled={disabledCategories}
                                    onToggle={toggleCategory}
                                />
                            )}

                            {customQuestions.length > 0 && (
                                <div className="GmList" style={{marginTop: 12}}>
                                    {customQuestions.map(q => (
                                        <div className="GmListItem" key={q.question}>
                                            <span className="GmListItem-name">{q.question}</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomQuestion(q.question)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow" style={{flexDirection: 'column', gap: 8, marginTop: 12}}>
                                <input
                                    className="GmInput"
                                    placeholder="Вопрос с пропуском (используй ____)"
                                    value={newQuestion}
                                    onChange={(e) => setNewQuestion(e.target.value)}
                                />
                                <input
                                    className="GmInput"
                                    placeholder="Настоящий ответ"
                                    value={newAnswer}
                                    onChange={(e) => setNewAnswer(e.target.value)}
                                />
                                <button className="GmBtn" onClick={addCustomQuestion}>Добавить вопрос</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activeQuestions.length === 0}
                                    onClick={startGame}
                                >
                                    Начать игру
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'collecting' && currentQuestion && (
                    <CollectAnswers
                        players={players}
                        placeholder="Твой правдоподобный вымышленный ответ"
                        prompt={
                            <>
                                <p className="GmHint">{currentQuestion.category}</p>
                                <p className="GmSituationText">{currentQuestion.question}</p>
                                <p className="GmHint" style={{marginTop: 10}}>Придумай правдоподобный ФЕЙКОВЫЙ ответ вместо пропуска</p>
                            </>
                        }
                        onDone={handleCollectDone}
                    />
                )}

                {stage === 'voting' && currentQuestion && (
                    <VoteAmongOptions
                        players={players}
                        options={options}
                        voteQuestion="Какой вариант, по-твоему, правда?"
                        prompt={<p className="GmSituationText">{currentQuestion.question}</p>}
                        onDone={handleVoteDone}
                    />
                )}

                {stage === 'result' && currentQuestion && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Ответ</h3>
                        <p className="GmSituationText" style={{marginBottom: 12}}>
                            {currentQuestion.question.replace('____', currentQuestion.answer)}
                        </p>

                        <div className="GmRevealList">
                            {options.map(o => {
                                const voters = votes
                                    .filter(v => v.optionId === o.id)
                                    .map(v => players.find(p => p.id === v.playerId)?.name)
                                    .filter(Boolean);
                                const authorName = o.authorId === null ? 'Правда' : (players.find(p => p.id === o.authorId)?.name || '?');
                                return (
                                    <div className={`GmRevealItem ${o.authorId === null ? 'GmRevealItem--flagged' : ''}`} key={o.id} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                                        <div><strong>{o.text}</strong> — {authorName}</div>
                                        {voters.length > 0 && <div className="GmHint">Голосовали: {voters.join(', ')}</div>}
                                    </div>
                                );
                            })}
                        </div>

                        <h3 className="GmCard-title" style={{marginTop: 16}}>Счёт</h3>
                        <div className="GmRevealList">
                            {ranking.map(p => (
                                <div className="GmRevealItem" key={p.id}>
                                    {p.name} — {scores[p.id] || 0}{roundPoints[p.id] ? ` (+${roundPoints[p.id]})` : ''}
                                </div>
                            ))}
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={nextRound}>Следующий вопрос</button>
                            <button className="GmBtn" onClick={backToSetup}>Закончить игру</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FibbageGame;
